import React, { useRef, useState } from 'react';
import {
  Brain,
  Image,
  ListChecks,
  MessageSquare,
  Sparkles,
  Upload,
  AlertCircle,
} from 'lucide-react';
import { Loader } from '../../components/ui';
import { cn } from '../../lib/utils';
import { createPortal } from 'react-dom';
import confetti from 'canvas-confetti';
import { validateImageFile, getAllowedImageFormatsDisplay } from '../../utils/imageValidation';

interface ProjectCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    name: string,
    description: string,
    context: string,
    avatarFile?: File
  ) => Promise<void>;
  isSubmitting?: boolean;
}

export function ProjectCreationModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ProjectCreationModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [context, setContext] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageValidationError, setImageValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || isSubmitting) return;

    try {
      await onSubmit(name, description, context, tempImageFile || undefined);

      // Fire confetti on success
      confetti({
        particleCount: 150,
        spread: 300,
        origin: { y: 0.3 },
        colors: ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8'],
        startVelocity: 45,
        gravity: 1.2,
        scalar: 1.2,
        ticks: 150,
      });

      // Reset form
      setName('');
      setDescription('');
      setContext('');
      setTempImageUrl(null);
      setTempImageFile(null);
      setImageError(false);
      setImageValidationError(null);

      onClose();
    } catch (error) {
      console.error('Error submitting project:', error);
      setImageError(true);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setImageValidationError(validation.error || 'Invalid file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setIsUploading(true);
      setImageError(false);
      setImageValidationError(null);

      // Cleanup previous temp URL
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
      }

      const previewUrl = URL.createObjectURL(file);
      setTempImageUrl(previewUrl);
      setTempImageFile(file);
    } catch (error) {
      console.error('Error handling project avatar:', error);
      setImageError(true);
      setTempImageUrl(null);
      setTempImageFile(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  const modal = (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
        className="fixed inset-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <div className="bg-white w-full sm:w-[95vw] max-w-5xl sm:rounded-xl shadow-xl max-h-[100vh] sm:max-h-[95vh] flex flex-col overflow-hidden">
          <form
            id="project-form"
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 relative"
          >
            {/* Title and close button */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2
                  id="modal-title"
                  className="text-xl sm:text-2xl font-bold text-gray-900 font-sora"
                >
                  Create New Project
                </h2>
                <p className="text-sm text-gray-500 mt-0.5 sm:mt-1">
                  Start organizing your ideas and conversations
                </p>
              </div>
            </div>

            {/* Project Benefits */}
            <div className="py-3 sm:py-4 bg-gray-50 rounded-lg mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 px-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Brain size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 font-sora">AI-Powered</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Intelligent assistance for your ideas
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageSquare size={18} className="text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 font-sora">
                      Organized Topics
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Keep conversations structured</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ListChecks size={18} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 font-sora">
                      Multiple Formats
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Chat, notes, lists, tasks and mood boards
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
              {/* Project Image - Mobile: top centered, Desktop: left column */}
              <div className="sm:col-span-3 flex flex-col items-center sm:items-start">
                <div className="block text-base font-bold text-gray-800 mb-1 font-sora">
                  Project Image
                </div>
                <div
                  className="relative w-24 h-24 mt-1 sm:w-full sm:h-[208px] rounded-2xl bg-gray-50 overflow-hidden cursor-pointer group shadow-md hover:shadow-lg transition-all duration-200"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                      <Loader color="text-blue-500" />
                    </div>
                  ) : tempImageUrl && !imageError ? (
                    <img
                      src={tempImageUrl}
                      alt="Project avatar preview"
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                      onLoad={() => setImageError(false)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={32} className="text-gray-400 sm:hidden" />
                      <Image size={40} className="text-gray-400 hidden sm:block" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Upload size={24} className="text-white" />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="avatar-input"
                  aria-label="Upload project avatar"
                  disabled={isSubmitting}
                />
                {imageValidationError && (
                  <div className="flex items-center gap-2 mt-2 text-red-500 text-xs sm:text-sm">
                    <AlertCircle size={14} className="flex-shrink-0" />
                    <span>{imageValidationError}</span>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1 text-center sm:text-left">
                  {getAllowedImageFormatsDisplay()} (max 10MB)
                </p>
              </div>

              {/* Project Inputs - Mobile: below image, Desktop: right columns */}
              <div className="sm:col-span-9">
                {/* Name and Description in one row with 40/60 split */}
                <div className="grid sm:grid-cols-10 gap-6">
                  <div className="sm:col-span-4">
                    <label
                      htmlFor="name"
                      className="block text-base font-bold text-gray-800 mb-2 font-sora"
                    >
                      Project Name*
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="w-full rounded-lg px-3 py-2 shadow-md bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      placeholder="Teleportation"
                      required
                      disabled={isSubmitting}
                      autoFocus
                    />
                  </div>

                  <div className="sm:col-span-6">
                    <label
                      htmlFor="description"
                      className="block text-base font-bold text-gray-800 mb-2 font-sora"
                    >
                      Short Description
                    </label>
                    <input
                      id="description"
                      type="text"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      className="w-full rounded-lg px-3 py-2 shadow-md bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      placeholder="Travel at lightspeed across the universe"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Context in a separate row */}
                <div className="mt-6">
                  <label
                    htmlFor="context"
                    className="block text-base font-bold text-gray-800 mb-2 font-sora"
                  >
                    Project Context
                  </label>
                  <textarea
                    id="context"
                    value={context}
                    onChange={e => setContext(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 shadow-md bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 resize-none"
                    placeholder="Write a quick summary of your project, so our AI can give you relevant responses. You'll be able to modify it later, and upload files for deeper understanding."
                    rows={4}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </form>

          {/* Action Buttons */}
          <div className="sticky bottom-0 left-0 right-0 p-4 sm:p-6 bg-white mt-1">
            <div className="flex gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'flex-1 sm:flex-initial px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm sm:text-base',
                  'hover:bg-gray-200 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="project-form"
                className={cn(
                  'flex-1 sm:flex-initial px-4 py-2 bg-blue-500 text-white rounded-lg text-sm sm:text-base',
                  'hover:bg-blue-600 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2 font-medium'
                )}
                disabled={!name.trim() || isUploading || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size="small" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>Create Project</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}
